import axios, { AxiosInstance } from "axios";

import LoginResponse from "@/types/response/login";
import { ServiceConfig } from "@/container/ServiceConfig";

export const ActionConstants = {
  LOGIN: "LOGIN",
  REGISTER: "REGISTER",
  UPDATE_PASSWORD: "UPDATE_PASSWORD",
  REQUEST_PASSWORD_RESET: "REQUEST_PASSWORD_RESET",
  RESET_PASSWORD: "RESET_PASSWORD",
  PORTAL_RESET_PASSWORD: "PORTAL_RESET_PASSWORD",
  VALIDATE_SELF_REGISTRATION: "VALIDATE_SELF_REGISTRATION",
  SELF_REGISTER: "SELF_REGISTER",
  REQUEST_SELF_REGISTRATION_EMAIL: "REQUEST_SELF_REGISTRATION_EMAIL",
  COMPLETE_SELF_REGISTRATION: "COMPLETE_SELF_REGISTRATION",
};

export default class LoginService {
  private readonly client: AxiosInstance;
  private readonly config: ServiceConfig;

  constructor(config: ServiceConfig) {
    if (!config.baseUrl) {
      throw new Error("You must specify a baseUrl in the service configuration");
    }

    this.config = config;
    this.client = axios.create({
      baseURL: config.baseUrl,
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Client-Origin": window.location.origin
      }
    })
  }

  private async createAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      "X-Client-Origin": window.location.origin
    };

    if (this.config.subscriptionKey) {
      headers['Ocp-Apim-Subscription-Key'] = this.config.subscriptionKey;
    }

    return headers;
  }

  async login(payload: unknown): Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("login", payload);
  }

  async register(payload: unknown): Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("login", payload);
  }

  async updatePassword(payload: unknown) : Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("updatePassword", payload);
  }
  async resetPassword(payload: unknown) : Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("portalResetPassword", payload);
  }
  async requestPasswordReset(payload: unknown) : Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("requestPasswordReset", payload);
  }
  async validateSelfRegistration(payload: unknown): Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("validateSelfRegistration", payload);
  }
  async selfRegister(payload: unknown): Promise<LoginResponse> {
    return this.submitWithAuth<LoginResponse>("selfRegister", payload);
  }

  // Helpers
  private async submitWithAuth<T>(endpoint: string, payload: unknown) {
    const headers = await this.createAuthHeaders();
    const url = `/v2/${endpoint}`;

    return new Promise<T>((resolve, reject) => {
      this.client
        .post(url, payload, { headers })
        .then(resp => {
          const data = resp.data;
          // If using subscription key, add it to the response for compatibility
          if (this.config.subscriptionKey && !data.error) {
            data.subscription_key = this.config.subscriptionKey;
          }
          resolve(data);
        })
        .catch(error => reject(error));
    });
  }
}